import { ApprovalDeviceIdentity } from '../devices/DeviceIdentity';
import type { JobBatchRequest, JobBatchView, JobView } from '@api/devices/contracts';
import { RUNBOOK_UPDATE_REQUIRED } from '@api/operations/runbook-compatibility';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Layers, ShieldCheck, X } from 'lucide-react';
import { Dialog } from 'radix-ui';
import { useEffect, useState } from 'react';
import { approveJobBatch, batchApprovalOptions, createJobBatch, isStatus, isRunbookUpdateRequired, rejectJobBatch } from '../lib/api';
import { absolute, duration } from '../lib/time';
import { describeKeyError, signWithKey } from '../lib/webauthn';
import { useScope } from '../scope/scope';
import { activityQueryKeys, useApprovalKeys, useJob, useJobBatch } from '../search/hooks';
import { Button } from '../ui/Button';
import { useToast } from '../ui/toast';
import { capabilitiesFrom, Fact, RegisterKeyButton, ReleaseFacts, useCapabilities } from './jobs';
import { JobParameters, JobStateBadge, ReviewList } from './meta';

const refreshJobs = (client: ReturnType<typeof useQueryClient>, mspId: string) =>
  client.invalidateQueries({ predicate: q => q.queryKey[0] === 'scope' && q.queryKey[1] === mspId
    && ['jobs', 'jobs-history', 'device-jobs', 'job', 'batch', ...activityQueryKeys, 'telemetry', 'agent-releases'].includes(String(q.queryKey[2])) });

/**
 * Ask for one runbook or agent update on several devices. Each device gets its own job; together they go to one
 * approval. Devices that can't take it are listed with why.
 */
export function BatchRequestDialog({ title, summary, action, deviceIds, open, onOpenChange, onNeedsApproval }: {
  title: string; summary: string; action: Omit<JobBatchRequest, 'deviceIds' | 'reason'>; deviceIds: string[];
  open: boolean; onOpenChange: (open: boolean) => void; onNeedsApproval: (batchId: string) => void;
}) {
  const { mspId } = useScope();
  const client = useQueryClient();
  const toast = useToast();
  const { approve: canApprove } = useCapabilities();
  const [reason, setReason] = useState('');
  const [result, setResult] = useState<JobBatchView | null>(null);
  useEffect(() => { if (open) { setReason(title); setResult(null); } }, [open, title]);
  const create = useMutation({
    mutationFn: () => createJobBatch(mspId, { ...action, deviceIds, reason: reason.trim() || title } as JobBatchRequest),
    onSuccess: batch => {
      void refreshJobs(client, mspId);
      const waiting = batch.jobs.some(j => j.state === 'awaiting_approval');
      if (!batch.skipped?.length && waiting && canApprove) { onOpenChange(false); onNeedsApproval(batch.id); return; }
      setResult(batch);
    },
    onError: error => toast({ tone: 'warning', title: 'Not requested',
      body: isRunbookUpdateRequired(error) ? RUNBOOK_UPDATE_REQUIRED : isStatus(error, 409) ? 'None of these devices can take this job.' : isStatus(error, 404) ? 'That release or runbook is no longer available.' : 'Try again.' }),
  });
  const waiting = result?.jobs.some(j => j.state === 'awaiting_approval');
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 animate-fade" />
        <Dialog.Content className="fixed inset-x-0 bottom-0 z-50 max-h-[90dvh] overflow-y-auto rounded-t-2xl border border-line bg-raised p-5 shadow-pop animate-pop sm:inset-auto sm:left-1/2 sm:top-[10vh] sm:w-[min(560px,calc(100vw-2rem))] sm:-translate-x-1/2 sm:rounded-2xl">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-[17px] font-semibold">{title} on {deviceIds.length} devices</Dialog.Title>
              <Dialog.Description className="mt-0.5 text-[13px] text-ink-2">{summary}</Dialog.Description>
            </div>
            <Dialog.Close className="rounded-md p-1 text-ink-3 hover:bg-hover" aria-label="Close"><X className="size-4" /></Dialog.Close>
          </div>
          {!result ? <>
            <label className="mt-4 block text-[13.5px]"><span className="text-ink-2">Reason</span>
              <input value={reason} onChange={e => setReason(e.target.value)} maxLength={500}
                className="mt-1 h-9 w-full rounded-lg border border-line bg-panel px-3 outline-none focus:border-brand/50" /></label>
            <p className="mt-3 text-[12px] text-ink-3">Each device gets its own job. One security-key approval covers exactly this set of jobs, and each device checks that it is one of them.</p>
            <div className="mt-4 flex justify-end gap-2">
              <Dialog.Close asChild><Button variant="ghost">Cancel</Button></Dialog.Close>
              <Button variant="primary" disabled={create.isPending} onClick={() => create.mutate()}><ShieldCheck className="size-4" />Request for {deviceIds.length} devices</Button>
            </div>
          </> : (
            <div className="mt-4 space-y-3 text-[13.5px]">
              <p>{result.jobs.length} job{result.jobs.length === 1 ? '' : 's'} requested.{result.skipped?.length ? ` ${result.skipped.length} device${result.skipped.length === 1 ? '' : 's'} couldn’t take it:` : ''}</p>
              {!!result.skipped?.length && (
                <ul className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-line px-3 py-2 text-[12.5px] text-ink-2">
                  {result.skipped.map(s => <li key={s.deviceId}><span className="font-mono">{s.deviceId.slice(0, 8)}</span> · {s.reason}</li>)}
                </ul>
              )}
              <div className="flex justify-end gap-2">
                <Dialog.Close asChild><Button variant="ghost">Close</Button></Dialog.Close>
                {waiting && canApprove && <Button variant="primary" onClick={() => { onOpenChange(false); onNeedsApproval(result.id); }}><KeyRound className="size-4" />Review and approve</Button>}
              </div>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * One approval for a batch: what runs, on which devices, then one key touch bound to exactly that set of jobs. Someone
 * who can't see every device in it can't sign the whole list, but may approve the ones they see one by one.
 */
export function BatchApprovalDialog({ batchId, open, onOpenChange, onApproveOne }: { batchId: string | null; open: boolean;
  onOpenChange: (open: boolean) => void; onApproveOne?: (job: JobView) => void }) {
  const { mspId, memberships } = useScope();
  const client = useQueryClient();
  const toast = useToast();
  const keys = useApprovalKeys();
  const batch = useJobBatch(open ? batchId : null);
  const sample = batch.data?.jobs[0];
  // The list leaves scripts out; read one job in full to show exactly what runs.
  const full = useJob(open && sample ? sample.id : null);
  const job = full.data ?? null;
  const { approve: canApprove } = useCapabilities();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  useEffect(() => setNote(''), [batchId, open]);
  const waiting = batch.data?.jobs.filter(j => j.state === 'awaiting_approval') ?? [];
  const decide = async (approve: boolean) => {
    if (!batchId) return;
    setBusy(true);
    try {
      if (approve) await approveJobBatch(mspId, batchId, await signWithKey(await batchApprovalOptions(mspId, batchId)));
      else await rejectJobBatch(mspId, batchId, note);
      await refreshJobs(client, mspId);
      toast({ title: approve ? 'Approved' : 'Rejected', body: `${waiting.length} job${waiting.length === 1 ? '' : 's'} ${approve ? 'queued' : 'rejected'}.` });
      onOpenChange(false);
    } catch (error) {
      toast({ tone: 'warning', title: approve ? 'Not approved' : 'Not rejected', body: (error as { status?: number })?.status === 403
        ? 'You need to see, and be allowed to approve on, every device in this batch.' : (error as { status?: number })?.status
          ? 'The server refused it. The batch may have changed; refresh and check.' : describeKeyError(error) });
    } finally { setBusy(false); }
  };
  const loaded = !!job && job.batchId === batchId;
  // The whole batch needs someone who sees every device and may approve on each; otherwise, one device at a time.
  const approvableAlone = (j: JobView) => j.state === 'awaiting_approval' && capabilitiesFrom(memberships, j).approve;
  const hidden = batch.data ? batch.data.size - batch.data.jobs.length : 0;
  const partial = !!batch.data && (hidden > 0 || waiting.some(j => !capabilitiesFrom(memberships, j).approve));
  const release = job?.operation === 'agent_update' ? job.release : null;
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/45 animate-fade" />
        <Dialog.Content className="fixed inset-x-0 bottom-0 z-50 flex max-h-[92dvh] flex-col overflow-hidden rounded-t-2xl border border-line bg-raised shadow-pop animate-pop sm:inset-auto sm:left-1/2 sm:top-[6vh] sm:w-[min(720px,calc(100vw-2rem))] sm:-translate-x-1/2 sm:rounded-2xl">
          <div className="flex items-start gap-3 border-b border-line px-5 py-4">
            <span className="mt-0.5 flex size-9 items-center justify-center rounded-xl bg-attention-soft text-attention"><Layers className="size-5" /></span>
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-[17px] font-semibold">Approve {batch.data?.title ?? 'this batch'} on {waiting.length} device{waiting.length === 1 ? '' : 's'}</Dialog.Title>
              <Dialog.Description className="text-[13px] text-ink-2">One key touch signs exactly these jobs. Each device checks it is one of them; nothing can be added afterwards.</Dialog.Description>
            </div>
            <Dialog.Close className="rounded-md p-1 text-ink-3 hover:bg-hover" aria-label="Close"><X className="size-4" /></Dialog.Close>
          </div>
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
            {job && (
              <dl className="divide-y divide-line/70 rounded-xl border border-line px-3">
                <Fact label="Runs as">{release ? `${job.shell === 'bash' || job.shell === 'zsh' ? 'root' : 'SYSTEM'} · replaces the WITTCO agent` : `${job.runAs === 'system' ? (job.shell === 'bash' || job.shell === 'zsh' ? 'root' : 'SYSTEM') : 'The signed-in user'} · ${job.shell === 'zsh' ? `Zsh · ${job.architecture}` : job.shell === 'bash' ? `Bash · ${job.architecture}` : '64-bit PowerShell'} · stops after ${duration(job.timeoutSeconds * 1000)}`}</Fact>
                <Fact label="Requested">{batch.data?.requestedByMe ? 'By you' : 'By a teammate'} — “{job.reason}”</Fact>
                <Fact label="Valid until">{absolute(job.expiresAt)}</Fact>
                {Object.keys(job.parameters).length > 0 && <Fact label="Parameters"><JobParameters parameters={job.parameters} /></Fact>}
              </dl>
            )}
            <section>
              <h3 className="mb-2 text-[13px] font-semibold text-ink-2">Devices · {batch.data?.jobs.length ?? '…'}</h3>
              <ul className="max-h-56 divide-y divide-line/70 overflow-y-auto rounded-xl border border-line">
                {batch.data?.jobs.map(j => (
                  <li key={j.id} className="flex items-center gap-2 px-3 py-2 text-[13px]">
                    <span className="min-w-0 flex-1"><ApprovalDeviceIdentity device={{ id: j.deviceId, title: j.deviceTitle ?? 'Removed device', hostname: j.deviceHostname }} />
                      <bdi dir="auto" className="text-ink-3">{[j.organizationName, j.siteName].filter(Boolean).join(' · ')}</bdi></span>
                    <JobStateBadge state={j.state} />
                    {partial && onApproveOne && approvableAlone(j) && (
                      <Button size="sm" variant="ghost" onClick={() => { onOpenChange(false); onApproveOne(j); }}>Approve alone</Button>
                    )}
                  </li>
                ))}
              </ul>
            </section>
            {release ? <ReleaseFacts release={release} /> : job && <>
              {job.review.length > 0 && <section><h3 className="mb-2 text-[13px] font-semibold text-ink-2">Review notes</h3><ReviewList findings={job.review} /></section>}
              <section>
                <h3 className="mb-1.5 text-[13px] font-semibold text-ink-2">Exact script</h3>
                {job.script !== null ? <pre className="max-h-72 overflow-auto rounded-xl border border-line bg-sunken p-3 font-mono text-[12.5px] leading-relaxed text-ink">{job.script}</pre>
                  : <p className="rounded-xl border border-line bg-sunken p-3 text-[13px] text-ink-3">Loading the exact script…</p>}
              </section>
            </>}
          </div>
          <div className="space-y-3 border-t border-line bg-sunken/40 px-5 py-4">
            {partial ? <p className="text-[13px] text-ink-2">This batch covers devices you {hidden > 0 ? 'can’t see' : 'can’t approve on'}, so you can’t approve it as a whole. Approve the devices you may one by one, or ask someone who can approve on them all.</p>
              : !canApprove ? <p className="text-[13px] text-ink-2">You can’t approve jobs in this workspace. Someone with approval rights will see it in Work.</p>
              : keys.data && keys.data.length === 0 ? (
                <div className="flex flex-wrap items-center gap-3"><p className="flex-1 text-[13px] text-ink-2">Approvals need a registered security key. Register one, then approve.</p><RegisterKeyButton variant="primary" /></div>
              ) : !waiting.length && batch.data ? <p className="text-[13px] text-ink-2">Nothing in this batch is waiting for approval.</p> : (
                <div className="flex flex-wrap items-center gap-2">
                  <input value={note} onChange={e => setNote(e.target.value)} placeholder="Note for a rejection (optional)" aria-label="Rejection note"
                    className="h-9 min-w-0 flex-1 rounded-lg border border-line bg-panel px-3 text-[13px] outline-none focus:border-brand/50" />
                  <Button variant="danger" disabled={busy || !loaded} onClick={() => void decide(false)}>Reject all</Button>
                  <Button variant="primary" disabled={busy || !loaded} onClick={() => void decide(true)}><ShieldCheck className="size-4" />{busy ? 'Touch your security key…' : 'Approve with security key'}</Button>
                </div>
              )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
